import React, { forwardRef, useRef } from "react";
import { useTranslation } from "react-i18next";
import "./LoadingElement.css";
import { LinearProgress } from "@mui/material";

const SHIRT_ICON_PATH = "/images/shirt.svg";

// Én fade-in pr. fulde sideload. Boot-splashene i App.tsx (BootSplash,
// UnauthenticatedView, PreLoader) er separate DOM-træer med samme udseende;
// ved skiftene remountes elementet, og en genstartet fade-in fra opacity 0
// var det grå glimt mellem dem. Alle mounts efter den første skipper derfor
// animationen, så remounts tager over usynligt. Initialiseres til true når
// index.html's statiske #km-splash allerede står på skærmen (modulet
// evalueres før React wiper #root) - den ER boot-forløbets første splash, så
// den første React-splash skal også tage over uden at dyppe spinneren.
let hasFadedInSincePageLoad = document.getElementById("km-splash") !== null;

interface LoadingElementProps extends React.HTMLAttributes<HTMLDivElement> {
  colorHex?: string;
  transparent?: boolean;
  showProgressBar?: boolean;
  progressBarValue?: number;
}

const LoadingElement = forwardRef<HTMLDivElement, LoadingElementProps>(
  (
    {
      className,
      transparent,
      showProgressBar,
      progressBarValue,
      colorHex,
      ...rest
    },
    ref
  ) => {
    const { t } = useTranslation();
    const loaderColor = colorHex ?? "#4b5563";

    // Afgøres ÉN gang pr. mount (ref-guard, stabil under StrictModes dobbelt-
    // render): en re-render midt i animationen må ikke pludselig slå den fra
    // og få opacity til at hoppe.
    const skipFadeRef = useRef<boolean | null>(null);
    if (skipFadeRef.current === null) {
      skipFadeRef.current = hasFadedInSincePageLoad;
      hasFadedInSincePageLoad = true;
    }

    return (
      <div
        ref={ref}
        id="preloader"
        {...rest}
        className={`!mt-0 h-full w-full flex justify-center items-center flex-col flex-1 ${
          transparent ? "bg-loadingTransparent" : "bg-loading"
        } z-50 ${skipFadeRef.current ? "preloader-no-fade " : ""}${
          className ?? ""
        }`}
      >
        <div className={"bg-white rounded-lg h-[80px] w-[80px] flex s"}>
          <div className={"flex w-full p-2"}>
            <div
              role="img"
              aria-label={t("compMisc.loadingShirtIcon")}
              className="h-full w-full"
              style={{
                backgroundColor: loaderColor,
                maskImage: `url(${SHIRT_ICON_PATH})`,
                WebkitMaskImage: `url(${SHIRT_ICON_PATH})`,
                maskRepeat: "no-repeat",
                WebkitMaskRepeat: "no-repeat",
                maskPosition: "center",
                WebkitMaskPosition: "center",
                maskSize: "contain",
                WebkitMaskSize: "contain",
              }}
            />
          </div>
        </div>
        {showProgressBar && (
          <LinearProgress
            variant={progressBarValue ? "determinate" : "indeterminate"}
            value={progressBarValue}
            sx={{ width: 100, marginTop: "24px" }}
          ></LinearProgress>
        )}
      </div>
    );
  }
);

export default LoadingElement;
